import { ColorName } from '@zapier/design-system';

/**
 * Subset of Zinnia colors allowed for section backgrounds\
 *  Selection based on How it Works and Demo Figmas
 *  https://www.figma.com/file/RNkmyPJm9yKmvE2m6lsDeo/How-it-Works?node-id=3111%3A1
 *  https://www.figma.com/file/C2BDpfXefJRYbPBxR7n9Km/Zapier-Demo?node-id=1643%3A201
 */
export type BackgroundColor = Extract<
  ColorName,
  | 'lava120'
  | 'lava80'
  | 'lava60'
  | 'lava40'
  | 'lava20'
  | 'lava10'
  | 'lava5'
  | 'lava'
  | 'ocean120'
  | 'ocean80'
  | 'ocean60'
  | 'ocean40'
  | 'ocean20'
  | 'ocean10'
  | 'ocean5'
  | 'ocean'
  | 'sun120'
  | 'sun80'
  | 'sun60'
  | 'sun40'
  | 'sun20'
  | 'sun10'
  | 'sun5'
  | 'sun'
  | 'white'
  | 'transparent'
  // V5 Colors
  | 'acidWash'
  | 'blue10'
  | 'cream'
  | 'gray'
  | 'moss'
  | 'neutral100'
  | 'neutral200'
  | 'neutral800'
  | 'night'
  | 'zapier'
  | 'earth'
>;

export const backgroundColors = {
  lava120: 'lava120',
  lava80: 'lava80',
  lava60: 'lava60',
  lava40: 'lava40',
  lava20: 'lava20',
  lava10: 'lava10',
  lava5: 'lava5',
  lava: 'lava',
  ocean120: 'ocean120',
  ocean80: 'ocean80',
  ocean60: 'ocean60',
  ocean40: 'ocean40',
  ocean20: 'ocean20',
  ocean10: 'ocean10',
  ocean5: 'ocean5',
  ocean: 'ocean',
  sun120: 'sun120',
  sun80: 'sun80',
  sun60: 'sun60',
  sun40: 'sun40',
  sun20: 'sun20',
  sun10: 'sun10',
  sun5: 'sun5',
  sun: 'sun',
  white: 'white',
  transparent: 'transparent',
} as const;

export const backgroundImages = {
  BlockyRetro: 'blockyRetro.png',
  Lava20Cropped: 'lava20Cropped.png',
  Sun40Cropped: 'sun40Cropped.png',
} as const;

export type BackgroundColorKeys = keyof typeof backgroundColors;

/**
 * Named background variations
 *  Used for backgrounds which aren't simply colors, allowing for implementing responsive image backgrounds in code allowing selection from "presets"
 */
export type BackgroundStyle =
  // https://cdn.zappy.app/fc03dfc5adfd97208bdfbef25150e968.png
  | 'Lava40Dotty'
  // https://cdn.zappy.app/ea787c1c8a501bd98ccc7644ecc9b78a.png
  | 'Sun60Dotty';

export type Background = BackgroundColor | BackgroundStyle | BackgroundImage;

export type BackgroundImage = keyof typeof backgroundImages;

export function isBackgroundStyle(
  background: string,
): background is BackgroundStyle {
  return background === 'Lava40Dotty' || background === 'Sun60Dotty';
}

export function mapBackgroundStyleToColor(
  background: BackgroundStyle,
): BackgroundColorKeys {
  const backgroundStyleNames = ['Dotty'];

  for (const name of backgroundStyleNames) {
    const color = background.split(name)[0].toLowerCase();
    if (color in backgroundColors) {
      return color as BackgroundColorKeys;
    }
  }
  return 'transparent';
}

/**
 * Narrow type predicate. Determines if is BackgroundImage
 * https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates
 */
export function isBackgroundImage(
  background: string,
): background is BackgroundImage {
  return background in backgroundImages;
}

export function getBackgroundImage(background: BackgroundImage): string {
  return backgroundImages[background];
}
